import React, { Fragment, useState, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import { Button } from 'reactstrap';
import { I } from '../../../common/v5/config';
import AgentGroupsForm from './agentGroups';
import AgentSkillsManager from '../../../components/agent/AgentSkillsManager';
import V5Input from '../../../components/form/V5Input';
import V5Select from '../../../components/form/V5Select';
import V5Switch from '../../../components/form/V5Switch';
import { EditorFooter, SaveButton, CancelButton, EditorHeader, editorTitleByView } from '../../../reactcomponents/Admin';
import { TableIconicButton, FormInputWithLabelRow } from '../../../reactcomponents/Form';
import AgentAvatarInput from '../../../reactcomponents/AgentAvatarInput';
import { NoFlipMultiSelect, SingleSelectDD } from '../../../reactcomponents/Dropdown';
import { formatValueToNameObj, dataURItoBlob } from '../../../common/v5/helpers';
import Feature from '../../../components/v5/Feature';
import CFlag from '../../../reactcomponents/CFlag';
import { AgentAccessKey } from '../../../components/v5/Agent';
import { DARK_MODE_APPEARANCE } from '../../../common/v5/constants';
import { LibrariesSelect } from '../../../reactcomponents/Chatbot';
import { useCallbackWithValue } from '../../../hooks/callback';
import { useSerialToArrayInt } from '../../../hooks/select';

const previewDelayOptions = Array.from({ length: 10 }, (_, i) => ({
  id: i,
  name: String(i)
}));

const activatePreviewOptions = [
  { id: 0, name: I('No') },
  { id: 1, name: I('Yes') },
  { id: 2, name: I('Delay') }
];

const errandListSizeOptions = [
  { id: 10, name: '10' },
  { id: 15, name: '15' },
  { id: 17, name: '17' },
  { id: 25, name: '25' },
  { id: 35, name: '35' },
  { id: 50, name: '50' }
];

// Same options as classic Agent.js maximumAllowedChat
const allowedChatAmountOptions = [
  { id: 0, name: '0' },
  ...Array.from({ length: 10 }, (_, i) => ({ id: i + 1, name: String(i + 1) })),
  { id: 9999, name: I('Unlimited') }
];

const labelChatbotLibraries = I('Chatbot libraries');

const ChatbotLibrariesSelect = ({ onChange, selected, data, hidden }) => {
  const onSelect = useCallbackWithValue(
    'automatedConversationBotLibraries',
    onChange,
    0
  );
  const [_selected, _onSelect] = useSerialToArrayInt(true, selected || '', onSelect);
  if (hidden) {
    return null;
  }
  return (
    <LibrariesSelect
      data={data}
      onSelect={_onSelect}
      selected={_selected}
      title={labelChatbotLibraries}
    />
  );
};

const AgentEditForm = ({
  hidden,
  input,
  activeId,
  view,
  isNew,
  onChangeAdminInput,
  onSave,
  onCancel,
  onDelete,
  onActivateAgent, // Add this prop for handling agent activation/deactivation
  adminStatus,
  areaList,
  availableAreas,
  langSrc,
  interfaceLangSrc,
  timeZone,
  salutationList,
  signatureList,
  availableSkills,
  proficiencyList,
  spellCheckLanguages,
  // Add adminLevelSrc prop
  adminLevelSrc,
  // Group-related props
  groupsAgentList,
  groupAgentListReady,
  onGetAgentGroups,
  onSaveAgentGroups,
  // Access key props
  accessKeyData,
  accessKeyURLCreator,
  onCreateAgentAccessKey,
  onDeleteAgentAccessKey,
  onLoadAgentAccessKeys,
  timezoneOffset,
  // Skills props
  onLoadSkills,
  onSaveAgentSkill,
  onRemoveAgentSkill,
  onUpdateAgentSkill,
  // Sysadmin access control
  isSysadmin,
  // Area-specific salutation and signature props
  selectedAreaSalutationList,
  selectedAreaSignatureList,
  areaSalutations,
  areaSignatures,
  onAddMP,
  onDeleteMP,
  onGetAreaSalutations,
  onGetAreaSignatures,
  chatbotProviders,
  chatbotLibraries,
  onLoadKnowledgeBaseList,
  multipleChatbotProviders,
  prefMode = false,
  listOfSoundOutputDevices = [],
  currentLogin = '',
  onCheckAgentEmailAvail,
  onValidateAgent,
  features = {},
  previewFeature = false,
  previewSummaryFeature = false,
  ...props
}) => {

  const [activeTab, setActiveTab] = useState('general');
  const [showArea, setShowArea] = useState(false);
  const [showManualArea, setShowManualArea] = useState(false);
  // Classic Agent.js: local UI only (Add button → show pickers), not saved toggles
  const [showAreaSalutation, setShowAreaSalutation] = useState(false);
  const [showAreaSignature, setShowAreaSignature] = useState(false);
  const [selectedAreaForSalutation, setSelectedAreaForSalutation] = useState(0);
  const [selectedAreaForSignature, setSelectedAreaForSignature] = useState(0);
  const [selectedSalutationForArea, setSelectedSalutationForArea] = useState(0);
  const [selectedSignatureForArea, setSelectedSignatureForArea] = useState(0);

  const flattenAreaList = (areas) => {
    if (!areas) {
      return [];
    }
    
    if (typeof areas === 'object' && !Array.isArray(areas) && areas.active) {
      areas = areas.active;
    }
    
    if (!Array.isArray(areas) || areas.length === 0) {
      return [];
    }
    
    const flatAreas = [];
    areas.forEach((org) => {
      if (org.Areas && Array.isArray(org.Areas)) {
        // Nested structure: [{Areas: [{Id, Name}]}]
        org.Areas.forEach(area => {
          flatAreas.push({
            id: area.Id || area.id,
            name: area.Name || area.name,
            value: area.Name || area.name
          });
        });
      } else if (org.Id || org.id) {
        // Already flat structure: [{Id, Name}]
        flatAreas.push({
          id: org.Id || org.id,
          name: org.Name || org.name,
          value: org.Name || org.name
        });
      }
    });
    
    return flatAreas;
  };
    const flattenedAreaList = useMemo(() => {
    // Try availableAreas first (from API response)
    let areasToFlatten = availableAreas;
    
    // If availableAreas is empty or not available, use areaList from Redux
    if (!areasToFlatten || (Array.isArray(areasToFlatten) && areasToFlatten.length === 0)) {
      areasToFlatten = areaList;
    }
    
    const flattened = flattenAreaList(areasToFlatten);
    return flattened;
  }, [areaList, availableAreas]);

  const chatbotProvidersList = useMemo(() => {
    const raw = chatbotProviders;
    if (raw == null) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  }, [chatbotProviders]);

  const conversationBotEnabled = useMemo(() => {
    if (input.automation !== undefined && input.automation !== null) {
      return !!input.automation;
    }
    const b = input.automatedConversationBot;
    if (b === true || b === 1) return true;
    if (typeof b === 'string') {
      const s = b.toLowerCase();
      return s === 'true' || s === '1';
    }
    return false;
  }, [input.automation, input.automatedConversationBot]);

  const showAutoAnswerFeature =
    !!features['machine-learning.auto-answer'] ||
    (!!features['amazon-comprehend'] && !!features['machine-learning.suggest-answer']);
  
  // Load skills data when component mounts or when editing an existing agent
  useEffect(() => {
    
    if (onLoadSkills && !isNew && activeId > 0) {
      try {
        onLoadSkills();
      } catch (error) {
        console.error('🔍 Error calling onLoadSkills:', error);
      }
    } else {
      console.log('🔍 Skills data loading skipped:', {
        onLoadSkills: !!onLoadSkills,
        isNew,
        activeId
      });
    }
  }, [onLoadSkills, isNew, activeId]);

  useEffect(() => {
    if (onLoadKnowledgeBaseList) {
      onLoadKnowledgeBaseList();
    }
  }, [onLoadKnowledgeBaseList]);

  const selectedBotProvider = input.automatedConversationBotProvider || 0;
  const showChatbotLibraries =
    conversationBotEnabled &&
    selectedBotProvider !== 4 &&
    chatbotLibraries &&
    chatbotLibraries.length > 0;
  
  const handleTextInputChange = (e) => {
    let field = e.target.name, value = e.target.value;
    
    // Special handling for numeric fields
    if (['allowedChatAmount'].includes(field)) {
      value = parseInt(value) || 0;
    }
    
    onChangeAdminInput(field, value);
  };

  const handleCheckboxChange = (field, value) => {
    if (field === 'automation' || field === 'automatedConversationBot') {
      const on = !!value;
      onChangeAdminInput('automatedConversationBot', on);
      onChangeAdminInput('automation', on);
      return;
    }

    onChangeAdminInput(field, value);
  };

  const NUMERIC_SELECT_FIELDS = new Set([
    'adminLevel',
    'inboxPreview',
    'inboxPreviewTimeDelay',
    'listSize',
    'errandListSize',
    'chatMessagePreview',
    'openExternalSystem',
    'interfaceLanguage',
    'spellingLanguage',
    'timezoneID',
    'allowedChatAmount',
    'automatedConversationBotProvider',
    'darkModeAppearance',
    'preferredManualArea',
    'salutation',
    'signature'
  ]);

  const handleSelectChange = (value, field) => {
    let next = value;
    if (NUMERIC_SELECT_FIELDS.has(field)) {
      next = parseInt(value, 10);
      if (Number.isNaN(next)) next = 0;
    }
    onChangeAdminInput(field, next);
  };

  const handleSkillsChange = (skills) => {
    onChangeAdminInput('skills', skills);
  };

  // Area selection handlers
  const handleSelectArea = (val) => {
    onChangeAdminInput('preferredArea', val);
  };

  const handleToggleArea = () => {
    setShowArea(!showArea);
  };

  const handleSelectManualArea = (val) => {
    onChangeAdminInput('preferredManualArea', val);
  };

  const handleToggleManualArea = () => {
    setShowManualArea(!showManualArea);
  };

  const handleSave = (...args) => {
    const event = args.find(arg => arg && typeof arg.preventDefault === 'function');
    if (event) {
      event.preventDefault();
    }
    onSave(input);
  };

  const toggle = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  // Avatar handlers
  const handleAvatarChange = (file) => {
    // Compress and resize image like the old Avatar component
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        // Create canvas to resize/compress image
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Set target dimensions (same as old implementation)
        const maxWidth = 160;
        const maxHeight = 120;
        
        // Calculate new dimensions maintaining aspect ratio
        let { width, height } = img;
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress image
        ctx.drawImage(img, 0, 0, width, height);
        const compressedDataURL = canvas.toDataURL('image/jpeg', 0.8); // 80% quality
        
        onChangeAdminInput('avatarPreview', compressedDataURL);
        // Upload avatar immediately like old implementation
        handleAvatarUpload(compressedDataURL);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleAvatarUpload = (preview) => {
    const fd = new FormData();
    let i = 0;
    let rand = Math.floor(Math.random() * 6) + '' + i + '' + Math.floor('' + new Date() / 1000);
    
    const blob = dataURItoBlob(preview);
    fd.append("uploadedAvatar", blob);
    fd.append("localName", rand);
    
    // Use jQuery ajax with same settings as RESTful postMultipart
    $.ajax({
      url: window.webserverRoot + "admin/agent/uploadAgentAvatar",
      data: fd,
      cache: false,
      contentType: false,
      processData: false,
      type: 'POST',
      success: function(rd) {
        // Store the uploaded avatar info for saving
        onChangeAdminInput('avatarLocalName', rd.id);
        onChangeAdminInput('avatarContentType', rd.value);
        console.log('Avatar uploaded successfully:', rd);
      },
      error: function(xhr, status, error) {
        console.error('Avatar upload failed:', error, xhr.responseText);
        // Optionally show user feedback
      }
    });
  };

  const handleAvatarRemove = () => {
    // Clear all avatar-related fields
    onChangeAdminInput('avatarPreview', '');
    onChangeAdminInput('avatarUrl', '');
    onChangeAdminInput('avatar', '');
    onChangeAdminInput('avatarLocalName', '');
    onChangeAdminInput('avatarContentType', '');
    
    // If editing existing agent, call remove API like old implementation
    if (!isNew && activeId > 0) {
      const fd = new FormData();
      fd.append("id", activeId);
      $.ajax({
        url: window.webserverRoot + "admin/agent/removeAgentAvatar",
        data: fd,
        cache: false,
        contentType: false,
        processData: false,
        type: 'POST',
        success: function(rd) {
          console.log('Avatar removed successfully:', rd);
        },
        error: function(xhr, status, error) {
          console.error('Avatar removal failed:', error, xhr.responseText);
        }
      });
    }
  };

  // Remove hardcoded adminLevelOptions and use API data instead
  const adminLevelOptions = formatValueToNameObj(adminLevelSrc) || [
    { id: 3, name: I('Agent') },           // Fallback only if API data is missing
    { id: 4, name: I('Observer') },        // Fallback only if API data is missing
    { id: 5, name: I('Administrator') }    // Fallback only if API data is missing
  ];
  
  // Try different data sources for interface language
  const interfaceLanguageOptions = formatValueToNameObj(interfaceLangSrc) || formatValueToNameObj(langSrc) || [];
  
  // Remove hardcoded defaultTimezones and use API data only
  const timezoneFormatted = formatValueToNameObj(timeZone);
  const timezoneOptions = timezoneFormatted || [];

  return (
    <div className="agent-edit-container" hidden={hidden}>
      {/* Header - Outside the shadowed form area */}
      <div className='admin-heading edit'>
        <div className='col-split-left'>
          <span className='admin-icons left mb-0'>
            <i className={classNames({ 'icon-edit': !isNew }, { 'icon-add': isNew })} />
          </span>
          <h5 className='d-inline'>{isNew ? I('Create Agent') : I('Edit Agent')}</h5>
        </div>
        <div className='col-split-right'>
          <TableIconicButton
            type='button'
            className='btn bg-transparent'
            iconClass='icon-times'
            title={I('Close form')}
            onClick={onCancel}
          />
        </div>
      </div>
      
      {/* Form content - Inside the shadowed area */}
      <div className="edit-admin-form agent-v5-form">
      <form id="agentEdit" className="admin-one-form">
      {/* Agent Overview Section */}
      <div className="agent-overview">
        <AgentAvatarInput
          currentAvatar={input.avatarPreview}
          onAvatarChange={handleAvatarChange}
          onAvatarRemove={handleAvatarRemove}
          />
        <div className="agent-basic-info">
          <div className="input-wrapper">
            <V5Input
              id="userName"
              label={I('Name')}
              name="userName"
              value={input.userName || ''}
              onChange={handleTextInputChange}
              required
              placeholder={I('Enter agent name')}
            />
            <V5Input
              id="email"
              label={I('Email')}
              name="email"
              value={input.email || ''}
              onChange={handleTextInputChange}
              onBlur={() => {
                if (onCheckAgentEmailAvail && input.email) {
                  onCheckAgentEmailAvail(activeId, input.email, prefMode);
                }
              }}
              required
              type="email"
              placeholder={I('Enter email address')}
            />
          </div>
        </div>
      </div>

      {/* Tabbed Section */}
      <div className="agent-tabs">
        <Nav tabs>
          <NavItem>
            <NavLink
              className={classNames({ active: activeTab === 'general' })}
              onClick={() => toggle('general')}
            >
              <i className="icon-user"></i>
              {I('General')}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classNames({ active: activeTab === 'groups' })}
              onClick={() => toggle('groups')}
            >
              <i className="icon-group"></i>
              {I('Groups')}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classNames({ active: activeTab === 'preferences' })}
              onClick={() => toggle('preferences')}
            >
              <i className="icon-settings"></i>
              {I('Preferences')}
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={activeTab}>
          {/* General Tab */}
          <TabPane tabId="general">
            <div className="tab-content-wrapper">
              <div className="form-section">
                <div className="v5-section-header">{I('General Information')}</div>
                
              <V5Input
                id="loginName"
                label={I('Login')}
                name="loginName"
                value={input.loginName || ''}
                onChange={handleTextInputChange}
                onBlur={() => {
                  if (onValidateAgent && input.loginName) {
                    onValidateAgent({
                      loginname: input.loginName,
                      agentId: activeId < 1 ? 0 : activeId
                    });
                  }
                }}
                required
                placeholder={I('Enter login name')}
              />
                
                <V5Input
                  id="password"
                  label={I('Password')}
                  name="password"
                  type="password"
                  value={input.password || ''}
                  onChange={handleTextInputChange}
                  required
                  placeholder={I('Enter password')}
                  helperText={I('Minimum 8 characters, include uppercase and number')}
              />
              
              <V5Select
                id="adminLevel"
                label={I('Administration Level')}
                name="adminLevel"
                value={input.adminLevel || 0}
                onChange={handleSelectChange}
                options={adminLevelOptions}
                placeholder={I('Select admin level')}
              />

              <CFlag flag="2024-03-08.CEN-1919.implement.login.with.otp">
                {features['agent.otp'] && input.adminLevel === 5 && (
                  <V5Input
                    id="phoneNumber"
                    label={I('Phone number')}
                    name="phoneNumber"
                    value={input.phoneNumber || ''}
                    onChange={handleTextInputChange}
                    required
                    placeholder={I('Enter phone number')}
                  />
                )}
              </CFlag>

              <V5Input
                id="externalID"
                label={I('External ID')}
                name="externalID"
                value={input.externalID || ''}
                onChange={handleTextInputChange}
                placeholder={I('Enter external ID')}
              />

              {/* Mark as Cention User - Sysadmin only */}
              {isSysadmin && (
                <V5Switch
                  id="markAsCentionUser"
                  label={I('Mark as Cention User')}
                  name="markAsCentionUser"
                  checked={input.markAsCentionUser || false}
                  onChange={(checked) => handleCheckboxChange('markAsCentionUser', checked)}
                />
              )}
              </div>

              {/* SIP Configuration Section */}
              <Feature tag="sip-enabled">
                <div className="form-section">
                  <div className="v5-section-header">{I('SIP Configuration')}</div>
                  
                  <V5Input
                    id="sipExtensionId"
                    label={I('SIP Extension ID')}
                    name="sipExtensionId"
                    value={input.sipExtensionId || ''}
                    onChange={handleTextInputChange}
                    placeholder={I('Enter SIP extension ID')}
                  />
                  
                  <V5Input
                    id="sipDeviceId"
                    label={I('SIP Device ID')}
                    name="sipDeviceId"
                    value={input.sipDeviceId || ''}
                    onChange={handleTextInputChange}
                    placeholder={I('Enter SIP device ID')}
                  />
                  
                  <V5Input
                    id="sipPassword"
                    label={I('SIP Password')}
                    name="sipPassword"
                    type="password"
                    value={input.sipPassword || ''}
                    onChange={handleTextInputChange}
                    placeholder={I('Enter SIP password')}
                  />
                  
                    {/* Classic Agent.js: ringing device only in prefMode */}
                    {!!prefMode && (
                      <V5Select
                        id="selectedSipRingOutput"
                        label={I('SIP Call - Select ringing device')}
                        name="selectedSipRingOutput"
                        value={input.selectedSipRingOutput || ''}
                        onChange={handleSelectChange}
                        options={(listOfSoundOutputDevices || []).map((d) => ({
                          id: d.id ?? d.value ?? d.name,
                          name: d.name || d.label || String(d.id ?? d.value ?? '')
                        }))}
                      />
                    )}
                  </div>
              </Feature>

              {/* System Integration Section */}
              {(window.features && (window.features['solidusEnabled'] || window.features['bright-integration'])) && (
              <div className="form-section">
                <div className="v5-section-header">{I('System Integration')}</div>
                
                <Feature tag="solidusEnabled">
                  <V5Input
                    id="solidusAgentID"
                    label={I('Solidus Agent ID')}
                    name="solidusAgentID"
                    type="number"
                    value={input.solidusAgentID || 0}
                    onChange={handleTextInputChange}
                    placeholder={I('Enter Solidus agent ID')}
                  />
                </Feature>
                
                <Feature tag="bright-integration">
                  <V5Input
                    id="brightAgentReferenceCode"
                    label={I('Bright Agent Reference Code')}
                    name="brightAgentReferenceCode"
                    value={input.brightAgentReferenceCode || ''}
                    onChange={handleTextInputChange}
                    placeholder={I('Enter Bright reference code')}
                  />
                </Feature>
              </div>
              )}

              {/* Skills — same gate as classic Agent.js */}
              {typeof cflag !== 'undefined' && cflag.IsActive('2023-09-29.CEN-1599.skills.admin.page') && !isNew && availableSkills && proficiencyList && onSaveAgentSkill && (
                <div className="form-section">
                  <div className="v5-section-header">{I('Skills')}</div>
                  <AgentSkillsManager
                    skills={input.skills || []}
                    onSkillsChange={handleSkillsChange}
                    availableSkills={availableSkills}
                    proficiencyList={proficiencyList}
                    onSaveAgentSkill={onSaveAgentSkill}
                    onRemoveAgentSkill={onRemoveAgentSkill}
                    onUpdateAgentSkill={onUpdateAgentSkill}
                    agentId={activeId}
                  />
                </div>
              )}
            </div>
          </TabPane>

          {/* Groups Tab */}
          <TabPane tabId="groups">
            <div className="tab-content-wrapper">
              <AgentGroupsForm
                activeId={activeId}
                adminStatus={adminStatus}
                groupsAgentList={groupsAgentList}
                groupAgentListReady={groupAgentListReady}
                onGetAgentGroups={onGetAgentGroups}
                onSaveAgentGroups={onSaveAgentGroups}
                onCancel={onCancel}
              />
            </div>
          </TabPane>

          {/* Preferences Tab */}
          <TabPane tabId="preferences">
            <div className="tab-content-wrapper">
              
              {/* Knowledge Base & Suggestions */}
              <Feature tag="cention-library">
                <Feature tag="alternative-answers">
                  <div className="form-section">
                    <div className="v5-section-header">{I('Knowledge Base & Suggestions')}</div>
                    
                    <V5Switch
                      id="autoInsertPublishedAnswer"
                      label={I('Automatically insert alternative answer')}
                      name="autoInsertPublishedAnswer"
                      checked={!!input.autoInsertPublishedAnswer}
                      onChange={(checked) => handleCheckboxChange('autoInsertPublishedAnswer', checked)}
                    />
                  </div>
                </Feature>
              </Feature>

              {showAutoAnswerFeature && (
                <div className="form-section">
                  <V5Switch
                    id="autoPickSuggestion"
                    label={I('Auto pickup suggested answer')}
                    name="autoPickSuggestion"
                    checked={!!input.autoPickSuggestion}
                    onChange={(checked) => handleCheckboxChange('autoPickSuggestion', checked)}
                  />
                </div>
              )}

              {/* Hotkeys Section */}
              <div className="form-section">
                <div className="v5-section-header">{I('Hotkeys')}</div>
                
                <V5Switch
                  id="hotkeys"
                  label={I('Activate hotkeys')}
                  name="hotkeys"
                  checked={!!input.hotkeys}
                  onChange={(checked) => handleCheckboxChange('hotkeys', checked)}
                />

                {!!input.hotkeys && (
                  <>
                    <V5Switch
                      id="hotkeys_markers"
                      label={I('Show hotkeys markers')}
                      name="hotkeys_markers"
                      checked={!!input.hotkeys_markers}
                      onChange={(checked) => handleCheckboxChange('hotkeys_markers', checked)}
                    />

                    <div className="modifiers-group">
                      <div className="v5-form-label">{I('Modifiers')}</div>
                      <div className="modifier-switches">
                        <V5Switch
                          id="hotkeys_ctrl"
                          label={I('Ctrl')}
                          name="hotkeys_ctrl"
                          checked={!!input.hotkeys_ctrl}
                          onChange={(checked) => handleCheckboxChange('hotkeys_ctrl', checked)}
                        />
                        <V5Switch
                          id="hotkeys_alt"
                          label={I('Alt')}
                          name="hotkeys_alt"
                          checked={!!input.hotkeys_alt}
                          onChange={(checked) => handleCheckboxChange('hotkeys_alt', checked)}
                        />
                        <V5Switch
                          id="hotkeys_shift"
                          label={I('Shift')}
                          name="hotkeys_shift"
                          checked={!!input.hotkeys_shift}
                          onChange={(checked) => handleCheckboxChange('hotkeys_shift', checked)}
                        />
                        <V5Switch
                          id="hotkeys_meta"
                          label={I('Meta')}
                          name="hotkeys_meta"
                          checked={!!input.hotkeys_meta}
                          onChange={(checked) => handleCheckboxChange('hotkeys_meta', checked)}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>

              {previewFeature && (
                <Feature tag="cention-workflow">
                  <div className="form-section">
                    <div className="v5-section-header">{I('Preview')}</div>

                    <V5Select
                      id="inboxPreview"
                      label={I('Activate preview')}
                      name="inboxPreview"
                      value={input.inboxPreview ?? 0}
                      onChange={handleSelectChange}
                      options={activatePreviewOptions}
                    />

                    {input.inboxPreview == 2 && (
                      <V5Select
                        id="inboxPreviewTimeDelay"
                        label={I('Delay time')}
                        name="inboxPreviewTimeDelay"
                        value={input.inboxPreviewTimeDelay ?? 0}
                        onChange={handleSelectChange}
                        options={previewDelayOptions}
                      />
                    )}

                    {previewSummaryFeature && (
                      <V5Switch
                        id="previewSummary"
                        label={I('Show Summary in preview')}
                        name="previewSummary"
                        checked={!!input.previewSummary}
                        onChange={(checked) => handleCheckboxChange('previewSummary', checked)}
                      />
                    )}
                  </div>
                </Feature>
              )}

              {/* Errand List Section */}
              <div className="form-section">
                <div className="v5-section-header">{I('Errand List')}</div>
                
                <V5Select
                  id="listSize"
                  label={I('Errand List Size')}
                  name="listSize"
                  value={input.listSize ?? input.errandListSize ?? 10}
                  onChange={handleSelectChange}
                  options={errandListSizeOptions}
                />
                
                <V5Select
                  id="spellingLanguage"
                  label={I('Preferred spell check language')}
                  name="spellingLanguage"
                  value={input.spellingLanguage || 1}
                  onChange={handleSelectChange}
                  options={formatValueToNameObj(spellCheckLanguages || langSrc)}
                />
                
                <V5Switch
                  id="sortHistoryAscending"
                  label={I('Sort History Ascending')}
                  name="sortHistoryAscending"
                  checked={!!input.sortHistoryAscending}
                  onChange={(checked) => handleCheckboxChange('sortHistoryAscending', checked)}
                />
              </div>

              {/* Area Preferences Section */}
              <div className="form-section">
                <div className="v5-section-header">{I('Area Preferences')}</div>
                
                <FormInputWithLabelRow label={I('Preferred area in all errands')}>
                  <NoFlipMultiSelect
                    flip={true}
                    id="agent-areas-v5"
                    title={I("Select areas")}
                    className="popup-multi-select"
                    overrideTitle={true}
                    groupSelect={true}
                    nested={{key: 'Areas'}}
                    data={availableAreas}
                    idFields={{id: "Id", name: "Name"}}
                    selected={input.preferredArea || []}
                    onSelect={handleSelectArea}
                    onToggle={handleToggleArea}
                    show={showArea}
                    selectAll={true}
                    selectNone={true}
                  />
                </FormInputWithLabelRow>
                
                <FormInputWithLabelRow label={I('Preferred area in manual errand')}>
                  <SingleSelectDD
                    id="manual-errand-areas-v5"
                    textNoItemSelected={I("Select area")}
                    className="popup-multi-select"
                    nested={{key: 'Areas'}}
                    data={availableAreas}
                    idFields={{id: "Id", name: "Name"}}
                    selected={input.preferredManualArea}
                    onSelect={handleSelectManualArea}
                    onToggle={handleToggleManualArea}
                    show={showManualArea}
                />
                </FormInputWithLabelRow>
              </div>

              {/* Salutation and Signature Section */}
              <Feature tag="agent.hide-personalisation-preview">
                <div className="form-section">
                  <div className="v5-section-header">{I('Salutation and Signature Preferences')}</div>
                  
                  <V5Switch
                    id="salutationPreview"
                    label={I('Show salutation preview')}
                    name="salutationPreview"
                    checked={!!input.salutationPreview}
                    onChange={(checked) => handleCheckboxChange('salutationPreview', checked)}
                />

                  <V5Switch
                    id="signaturePreview"
                    label={I('Show signature preview')}
                    name="signaturePreview"
                    checked={!!input.signaturePreview}
                    onChange={(checked) => handleCheckboxChange('signaturePreview', checked)}
                  />
                  
                  <V5Select
                    id="salutation"
                    label={I('Preferred salutation')}
                    name="salutation"
                    value={input.salutation || 0}
                    onChange={handleSelectChange}
                    options={formatValueToNameObj(salutationList)}
                  />
                  
                  <V5Select
                    id="signature"
                    label={I('Preferred signature')}
                    name="signature"
                    value={input.signature || 0}
                    onChange={handleSelectChange}
                    options={formatValueToNameObj(signatureList)}
                  />
                  
                  <V5Input
                    id="signatureName"
                    label={I('Signature Name')}
                    name="signatureName"
                    value={input.signatureName || ''}
                    onChange={handleTextInputChange}
                    placeholder={I('Enter signature name')}
                  />

                  {/* Classic: Add button opens pickers; list persists via onAddMP/onDeleteMP */}
                  <div style={{ marginTop: '12px' }}>
                    <Button
                      type="button"
                      color="primary"
                      className="btn-agent-inner"
                      onClick={() => setShowAreaSalutation(true)}
                    >
                      {I('Add area specific salutation')}
                    </Button>
                  </div>

                  {selectedAreaSalutationList && selectedAreaSalutationList.length > 0 && (
                    <div className="area-salutation-list" style={{ marginTop: '10px', marginBottom: '10px' }}>
                      <table className="table" style={{ fontSize: '12px' }}>
                        <thead>
                          <tr>
                            <th>{I('Area')}</th>
                            <th>{I('Salutation')}</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedAreaSalutationList.map((item) => (
                            <tr key={item.id}>
                              <td>{item.areaName || item.area_name || ''}</td>
                              <td>{item.personalisationName || item.personalisation_name || ''}</td>
                              <td>
                                <Button
                                  type="button"
                                  size="sm"
                                  color="danger"
                                  onClick={() => onDeleteMP && onDeleteMP(item.id, 1)}
                                  style={{ padding: '2px 8px', fontSize: '11px' }}
                                >
                                  ×
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {showAreaSalutation && (
                    <div className="sub-label-row" style={{ marginTop: '10px' }}>
                      <V5Select
                        id="areaSalutation"
                        label={I('Choose Area')}
                        name="areaSalutation"
                        value={selectedAreaForSalutation || 0}
                        onChange={(value) => {
                          const areaId = parseInt(value, 10) || 0;
                          setSelectedAreaForSalutation(areaId);
                          setSelectedSalutationForArea(0);
                          if (areaId > 0 && onGetAreaSalutations) {
                            onGetAreaSalutations(areaId);
                          }
                        }}
                        options={flattenedAreaList.length > 0 ? formatValueToNameObj(flattenedAreaList, 'id', 'name') : []}
                      />

                      {selectedAreaForSalutation > 0 && (
                        <>
                          <V5Select
                            key={`area-salutation-${selectedAreaForSalutation}-${areaSalutations?.length || 0}`}
                            id="prefAreaSalutation"
                            label={I('Choose Salutation')}
                            name="prefAreaSalutation"
                            value={selectedSalutationForArea || 0}
                            onChange={(value) => setSelectedSalutationForArea(parseInt(value, 10) || 0)}
                            options={formatValueToNameObj(
                              (areaSalutations && areaSalutations.length > 0) ? areaSalutations : salutationList
                            )}
                          />
                          <Button
                            type="button"
                            color="primary"
                            size="sm"
                            onClick={() => {
                              if (selectedAreaForSalutation > 0 && selectedSalutationForArea > 0 && onAddMP) {
                                onAddMP(selectedSalutationForArea, 1, selectedAreaForSalutation);
                                setSelectedAreaForSalutation(0);
                                setSelectedSalutationForArea(0);
                              }
                            }}
                            disabled={!selectedAreaForSalutation || !selectedSalutationForArea}
                            style={{ marginTop: '10px' }}
                          >
                            +
                          </Button>
                        </>
                      )}
                    </div>
                  )}

                  <div style={{ marginTop: '12px' }}>
                    <Button
                      type="button"
                      color="primary"
                      className="btn-agent-inner"
                      onClick={() => setShowAreaSignature(true)}
                    >
                      {I('Add area specific signature')}
                    </Button>
                  </div>

                  {selectedAreaSignatureList && selectedAreaSignatureList.length > 0 && (
                    <div className="area-signature-list" style={{ marginTop: '10px', marginBottom: '10px' }}>
                      <table className="table" style={{ fontSize: '12px' }}>
                        <thead>
                          <tr>
                            <th>{I('Area')}</th>
                            <th>{I('Signature')}</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedAreaSignatureList.map((item) => (
                            <tr key={item.id}>
                              <td>{item.areaName || item.area_name || ''}</td>
                              <td>{item.personalisationName || item.personalisation_name || ''}</td>
                              <td>
                                <Button
                                  type="button"
                                  size="sm"
                                  color="danger"
                                  onClick={() => onDeleteMP && onDeleteMP(item.id, 2)}
                                  style={{ padding: '2px 8px', fontSize: '11px' }}
                                >
                                  ×
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {showAreaSignature && (
                    <div className="sub-label-row" style={{ marginTop: '10px' }}>
                      <V5Select
                        id="areaSignature"
                        label={I('Choose Area')}
                        name="areaSignature"
                        value={selectedAreaForSignature || 0}
                        onChange={(value) => {
                          const areaId = parseInt(value, 10) || 0;
                          setSelectedAreaForSignature(areaId);
                          setSelectedSignatureForArea(0);
                          if (areaId > 0 && onGetAreaSignatures) {
                            onGetAreaSignatures(areaId);
                          }
                        }}
                        options={flattenedAreaList.length > 0 ? formatValueToNameObj(flattenedAreaList, 'id', 'name') : []}
                      />

                      {selectedAreaForSignature > 0 && (
                        <>
                          <V5Select
                            id="prefAreaSignature"
                            label={I('Choose Signature')}
                            name="prefAreaSignature"
                            value={selectedSignatureForArea || 0}
                            onChange={(value) => setSelectedSignatureForArea(parseInt(value, 10) || 0)}
                            options={formatValueToNameObj(
                              (areaSignatures && areaSignatures.length > 0) ? areaSignatures : signatureList
                            )}
                          />
                          <Button
                            type="button"
                            color="primary"
                            size="sm"
                            onClick={() => {
                              if (selectedAreaForSignature > 0 && selectedSignatureForArea > 0 && onAddMP) {
                                onAddMP(selectedSignatureForArea, 2, selectedAreaForSignature);
                                setSelectedAreaForSignature(0);
                                setSelectedSignatureForArea(0);
                              }
                            }}
                            disabled={!selectedAreaForSignature || !selectedSignatureForArea}
                            style={{ marginTop: '10px' }}
                          >
                            +
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </Feature>

              {/* Chat Preferences Section */}
              <Feature tag="chat">
                <div className="form-section">
                  <div className="v5-section-header">{I('Chat Preferences')}</div>

                <V5Input
                    id="chatName"
                    label={I('Chat alias')}
                    name="chatName"
                    value={input.chatName || ''}
                  onChange={handleTextInputChange}
                    placeholder={I('Enter chat display name')}
                />
                  
                  <V5Switch
                    id="playSound"
                    label={I('Play sound for new chat message')}
                    name="playSound"
                    checked={!!input.playSound}
                    onChange={(checked) => handleCheckboxChange('playSound', checked)}
                />

                <V5Select
                    id="allowedChatAmount"
                    label={I('Number of allowed simultaneous chat sessions')}
                    name="allowedChatAmount"
                    value={input.allowedChatAmount ?? 0}
                    onChange={handleSelectChange}
                    options={allowedChatAmountOptions}
                />

                  <V5Select
                    id="chatMessagePreview"
                    label={I('Chat message preview')}
                    name="chatMessagePreview"
                    value={input.chatMessagePreview || 0}
                    onChange={handleSelectChange}
                    options={input.chatMessagePreviewOptions || [
                      { id: 0, name: I('See message') },
                      { id: 1, name: I('Only Notification') }
                    ]}
                />
                </div>
              </Feature>

              {/* Advanced Preferences — classic Agent.js has no showOnlyWarnings UI */}

              {/* Notification Preferences */}
              <div className="form-section">
                <div className="v5-section-header">{I('Notification Preferences')}</div>
                
                <V5Switch
                  id="blueBubbleNotification"
                  label={I('Blue Bubble Notifications')}
                  name="blueBubbleNotification"
                  checked={input.blueBubbleNotification !== false && input.blueBubbleNotification !== 0}
                  onChange={(checked) => handleCheckboxChange('blueBubbleNotification', checked)}
                />
                
                <V5Switch
                  id="browserOsNotification"
                  label={I('Browser OS Notifications (overrides Blue Bubble)')}
                  name="browserOsNotification"
                  checked={input.browserOsNotification || false}
                  onChange={(checked) => handleCheckboxChange('browserOsNotification', checked)}
                />
              </div>

              {/* Chatbot — classic: editable only for sysadmin creating new agent */}
              {chatbotProvidersList.length > 0 && (() => {
                const botReadOnly = !!prefMode || activeId > 0 || currentLogin !== 'sysadmin';
                if (botReadOnly) {
                  return (
                    <div className="form-section">
                      <div className="v5-section-header">{I('Automated Conversation Bot')}</div>
                      <div className="v5-form-label">
                        {I('Automated Conversation Bot')}: {conversationBotEnabled ? I('Yes') : I('No')}
                      </div>
                    </div>
                  );
                }
                return (
                <div className="form-section">
                  <div className="v5-section-header">{I('Automated Conversation Bot')}</div>

                  <V5Switch
                    id="automatedConversationBot"
                    label={I('Automated Conversation Bot')}
                    name="automatedConversationBot"
                    checked={conversationBotEnabled}
                    onChange={(checked) => handleCheckboxChange('automatedConversationBot', checked)}
                  />

                  {conversationBotEnabled && chatbotProvidersList.length > 0 && (
                    <V5Select
                      id="automatedConversationBotProvider"
                      label={I('Provider')}
                      name="automatedConversationBotProvider"
                      placeholder={I('Select a provider')}
                      value={
                        input.automatedConversationBotProvider > 0
                          ? input.automatedConversationBotProvider
                          : ''
                      }
                      onChange={handleSelectChange}
                      options={chatbotProvidersList.map((p) => ({
                        id: p.id,
                        name: p.name || p.Name || ''
                      }))}
                    />
                  )}

                  <ChatbotLibrariesSelect
                    data={chatbotLibraries}
                    hidden={!showChatbotLibraries}
                    onChange={onChangeAdminInput}
                    selected={input.automatedConversationBotLibraries}
                  />
                </div>
                );
              })()}

              {isSysadmin && showAutoAnswerFeature && (
                <div className="form-section">
                  <div className="v5-section-header">{I('Automation')}</div>
                  <V5Switch
                    id="autoAnswer"
                    label={I('Auto Answer')}
                    name="autoAnswer"
                    checked={input.autoAnswer || false}
                    onChange={(checked) => handleCheckboxChange('autoAnswer', checked)}
                  />
                </div>
              )}

              {/* Quick Search Section */}
              <Feature tag="quicksearch.enabled">
                <div className="form-section">
                  <div className="v5-section-header">{I('Quick Search Settings')}</div>
                  
                  <V5Switch
                    id="doQuickSearch"
                    label={I('Enable Quick Search')}
                    name="doQuickSearch"
                    checked={input.doQuickSearch || false}
                    onChange={(checked) => handleCheckboxChange('doQuickSearch', checked)}
                  />
                  
                  {input.doQuickSearch && (
                    <V5Input
                      id="quickSearchAgentReferenceCode"
                      label={I('Quick Search Reference Code')}
                      name="quickSearchAgentReferenceCode"
                      value={input.quickSearchAgentReferenceCode || ''}
                      onChange={handleTextInputChange}
                      placeholder={I('Enter quick search reference code')}
                    />
                  )}
                </div>
              </Feature>

              {/* System Preferences Section */}
              <div className="form-section">
                <div className="v5-section-header">{I('System Preferences')}</div>

              <V5Select
                id="interfaceLanguage"
                  label={I('Language')}
                name="interfaceLanguage"
                value={input.interfaceLanguage || 1}
                onChange={handleSelectChange}
                options={interfaceLanguageOptions}
                />
                
                <V5Select
                  id="darkModeAppearance"
                  label={I('Dark Mode Appearance')}
                  name="darkModeAppearance"
                  value={input.darkModeAppearance || 0}
                  onChange={handleSelectChange}
                  options={DARK_MODE_APPEARANCE}
              />

                <Feature tag="open-external-system">
                  <V5Select
                    id="openExternalSystem"
                    label={I('Open external system')}
                    name="openExternalSystem"
                    value={input.openExternalSystem || 0}
                    onChange={handleSelectChange}
                    options={input.openExternalSystemOptions || [
                      { id: 0, name: I('Disabled') },
                      { id: 1, name: I('Manual (New tab)') },
                      { id: 2, name: I('Auto (New tab)') },
                      { id: 3, name: I('Auto (New window)') }
                    ]}
                  />
                </Feature>

              <Feature tag="show-time-zone">
                <V5Select
                  id="timezoneID"
                    label={I('Timezone')}
                  name="timezoneID"
                  value={input.timezoneID || 14}
                  onChange={handleSelectChange}
                  options={formatValueToNameObj(timeZone)}
                />
              </Feature>

              <V5Input
                  id="allowedIp"
                  label={I('Allowed IP')}
                  name="allowedIp"
                  value={input.allowedIp || ''}
                onChange={handleTextInputChange}
                  placeholder={I('Enter allowed IP addresses (comma separated)')}
                  helperText={I('Leave empty to allow all IPs')}
              />

                {!isNew && (
                  <div className="access-key-section">
                    <div className="v5-section-header">{I('Access Keys')}</div>
  
                    {accessKeyData && accessKeyURLCreator && onCreateAgentAccessKey && onDeleteAgentAccessKey && onLoadAgentAccessKeys ? (
                      <AgentAccessKey
                        agentID={activeId}
                        accessKeyURLCreator={accessKeyURLCreator}
                        data={accessKeyData}
                        onCreate={onCreateAgentAccessKey}
                        onDelete={onDeleteAgentAccessKey}
                        onLoad={onLoadAgentAccessKeys}
                        timezoneOffset={timezoneOffset || 0}
                      />
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          </TabPane>
        </TabContent>
      </div>

      {/* Action Buttons */}
      <div className='agent-actions'>
        {!isNew && (
          <>
            <Button
              title={I("Remove agent")}
              className={'btn-text text-normal'}
              onClick={() => onDelete(activeId)}
            >
              <i className="icon-remove"></i>
              {I("Remove")}
            </Button>
            {onActivateAgent && (
              <Button
                title={input.active ? I("Deactivate agent") : I("Activate agent")}
                className={'btn-text text-danger'}
                onClick={() => onActivateAgent(!input.active, activeId)}
              >
                <i className={input.active ? "icon-trash" : "icon-check"}></i>
                {input.active ? I("Deactivate agent") : I("Activate agent")}
              </Button>
            )}
          </>
        )}
      </div>

      <EditorFooter hidden={false}>
        <SaveButton
          onClick={handleSave}
          saving={adminStatus === 'saving'}
          text={I('Save')}
        />
        <CancelButton
          onClick={onCancel}
          text={I('Cancel')}
        />
      </EditorFooter>
      </form>
      </div>
    </div>
  );
};

export default AgentEditForm; 